import { Plus, Trash2 } from "lucide-react";
import type { UseFormWatch, UseFormSetValue } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOfficeStore } from "@/features/projects/useOfficeStore";
import { newMapaRow } from "./newMapaRow";
import type { ProjectDataInput } from "./schema";

/**
 * R8 — mape (project folders) editor. Optional: an empty list produces no
 * Popis mapa / Sadržaj Mape 1 documents (existing projects unchanged). The
 * Sadržaj's opći dio is derived server-side from the active slot registry;
 * only the mapa records (struka / izrađivač / projektant / oznaka) and the
 * shared project mark are entered here.
 */
export function MapeFields({
  watch,
  setValue,
}: {
  watch: UseFormWatch<ProjectDataInput>;
  setValue: UseFormSetValue<ProjectDataInput>;
}) {
  const mape = watch("mape") ?? [];
  const zajednicka = watch("zajednicka_oznaka") ?? "";
  const office = useOfficeStore((s) => s.office);
  const dirty = () => ({ shouldDirty: true });

  const patch = (i: number, next: Record<string, unknown>) => {
    const copy = [...mape];
    copy[i] = { ...copy[i], ...next } as (typeof mape)[number];
    setValue("mape", copy, dirty());
  };

  return (
    <div className="mt-4 flex flex-col gap-4 border-t border-stone-100 pt-4">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <Label htmlFor="zajednicka_oznaka">Zajednička oznaka projekta</Label>
          <Input
            id="zajednicka_oznaka"
            placeholder="npr. GP-22/02/25"
            value={zajednicka}
            onChange={(e) => setValue("zajednicka_oznaka", e.target.value, dirty())}
          />
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            // S1 — a new row is pre-seeded from the office profile
            // (fill-empty: only ever a NEW row, never existing values).
            setValue("mape", [...mape, newMapaRow(mape.length, office)], dirty())
          }
        >
          <Plus className="h-3.5 w-3.5" /> Dodaj mapu
        </Button>
      </div>

      {mape.length === 0 ? (
        <p className="text-[0.74rem] text-stone-400">
          Bez mapa — ništa se ne dodaje u dokumentaciju. Dodaj mape (po
          strukama) da se generiraju „Popis mapa" i „Sadržaj Mape 1".
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {mape.map((m, i) => (
            <li
              key={i}
              className="rounded-md border border-stone-200 bg-stone-50/60 p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[0.78rem] font-semibold text-stone-600">
                  Mapa {m.redni_broj || i + 1}
                </span>
                <button
                  type="button"
                  title="Ukloni mapu"
                  className="rounded p-1 text-stone-400 hover:bg-red-50 hover:text-red-600"
                  onClick={() =>
                    setValue("mape", mape.filter((_, j) => j !== i), dirty())
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Input
                  placeholder="Redni broj (npr. 1)"
                  value={m.redni_broj ?? ""}
                  onChange={(e) => patch(i, { redni_broj: e.target.value })}
                />
                <Input
                  className="sm:col-span-2"
                  placeholder="Struka (npr. Arhitektonski projekt)"
                  value={m.struka ?? ""}
                  onChange={(e) => patch(i, { struka: e.target.value })}
                />
                <Input
                  placeholder="Oznaka projekta (npr. GP-22/02/25 A)"
                  value={m.oznaka_projekta ?? ""}
                  onChange={(e) => patch(i, { oznaka_projekta: e.target.value })}
                />
                <Input
                  placeholder="Izrađivač (tvrtka)"
                  value={m.izradivac?.tvrtka ?? ""}
                  onChange={(e) =>
                    patch(i, {
                      izradivac: { ...m.izradivac, tvrtka: e.target.value },
                    })
                  }
                />
                <Input
                  placeholder="Izrađivač (adresa)"
                  value={m.izradivac?.adresa ?? ""}
                  onChange={(e) =>
                    patch(i, {
                      izradivac: { ...m.izradivac, adresa: e.target.value },
                    })
                  }
                />
                <Input
                  placeholder="Projektant (ime)"
                  value={m.projektant?.ime ?? ""}
                  onChange={(e) =>
                    patch(i, {
                      projektant: { ...m.projektant, ime: e.target.value },
                    })
                  }
                />
                <Input
                  placeholder="Titula (npr. mag.inž.arh.)"
                  value={m.projektant?.titula ?? ""}
                  onChange={(e) =>
                    patch(i, {
                      projektant: { ...m.projektant, titula: e.target.value },
                    })
                  }
                />
                <Input
                  placeholder="Br. ovlaštenja"
                  value={m.projektant?.broj_ovlastenja ?? ""}
                  onChange={(e) =>
                    patch(i, {
                      projektant: {
                        ...m.projektant,
                        broj_ovlastenja: e.target.value,
                      },
                    })
                  }
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
